import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SiteLeads } from "@/components/mascot/SiteLeads";
import { ProblemReports } from "@/components/ProblemReports";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Megaphone, Send, Loader2, BellRing, BellOff, CheckCheck, Search, History } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");
const TYPE_LABEL = {
  aviso: "Aviso", status_change: "Status", budget_created: "Novo orçamento", admin_message: "Mensagem",
  item_added: "Item adicionado", item_updated: "Item alterado", item_removed: "Item removido",
  item_completed: "Item concluído", item_reopened: "Item reaberto", item_date_changed: "Data alterada",
  concorrencias_available: "Concorrências", validity_warning: "Validade", validity_expired: "Validade expirada",
  item_photo: "Foto do item", paperwork_done: "Documentação", reopened: "Reaberto",
};

const SchoolPicker = ({ schools, pushSet, selected, setSelected }) => {
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? schools.filter((s) => (s.name || "").toLowerCase().includes(t) || (s.nickname || "").toLowerCase().includes(t)) : schools;
  }, [schools, q]);
  return (
    <div className="rounded-xl border border-slate-200">
      <div className="p-2 border-b flex items-center gap-2">
        <Search className="w-4 h-4 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar escola..." className="w-full outline-none text-sm" data-testid="notice-school-search" />
        <span className="text-[11px] text-slate-500 shrink-0">{selected.size} selec.</span>
      </div>
      <ul className="max-h-64 overflow-y-auto divide-y divide-slate-100">
        {list.map((s) => (
          <li key={s.id}>
            <label className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`notice-school-${s.id}`}>
              <Checkbox checked={selected.has(s.id)} onCheckedChange={() => setSelected((prev) => { const n = new Set(prev); n.has(s.id) ? n.delete(s.id) : n.add(s.id); return n; })} />
              <span className="flex-1 min-w-0 truncate">{s.name}</span>
              {pushSet.has(s.id) ? <BellRing className="w-3.5 h-3.5 text-emerald-600" title="Push ativo" /> : <BellOff className="w-3.5 h-3.5 text-slate-300" title="Sem push" />}
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
};

const SendForm = ({ schools, stats, onSent }) => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [all, setAll] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [sending, setSending] = useState(false);
  const pushSet = useMemo(() => new Set(stats?.school_ids_with_push || []), [stats]);

  const send = async () => {
    if (!title.trim() || !body.trim()) return toast.error("Informe título e mensagem");
    if (!all && selected.size === 0) return toast.error("Escolha pelo menos uma escola");
    const n = all ? schools.length : selected.size;
    if (!window.confirm(`Enviar este aviso para ${n} escola(s)?`)) return;
    setSending(true);
    try {
      const r = await api.post("/admin/notices/broadcast", { title, body, all_schools: all, school_ids: [...selected] });
      toast.success(`Aviso enviado para ${r.data.recipients} escola(s) — ${r.data.push_recipients} com push no celular`);
      setTitle(""); setBody(""); setSelected(new Set());
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar");
    } finally { setSending(false); }
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 card-shadow space-y-3" data-testid="notice-send-form">
      <h2 className="font-heading text-base font-black text-slate-900 flex items-center gap-2"><Megaphone className="w-4 h-4 text-orange-600" /> Enviar aviso</h2>
      <div>
        <label className="block text-xs font-bold text-slate-600 mb-1">Título</label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} placeholder="Ex.: Recesso de fim de ano" data-testid="notice-title" />
      </div>
      <div>
        <label className="block text-xs font-bold text-slate-600 mb-1">Mensagem</label>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} maxLength={500} placeholder="Escreva o aviso para as escolas" data-testid="notice-body" />
      </div>
      <div className="flex flex-col gap-2">
        {[[true, `Todas as escolas (${schools.length})`], [false, "Escolher escolas"]].map(([v, l]) => (
          <button key={String(v)} type="button" onClick={() => setAll(v)} data-testid={`notice-target-${v ? "all" : "some"}`}
            className={`h-10 w-full rounded-md border text-sm font-bold transition-colors ${all === v ? "bg-[#1e3a8a] border-[#1e3a8a] text-white" : "bg-white border-slate-200 text-slate-700"}`}>
            {l}
          </button>
        ))}
      </div>
      {!all && <SchoolPicker schools={schools} pushSet={pushSet} selected={selected} setSelected={setSelected} />}
      <p className="text-[11px] text-slate-500">O aviso aparece no sininho do portal da escola. Escolas com push ativo (<BellRing className="w-3 h-3 inline text-emerald-600" />) recebem também no celular.</p>
      <Button onClick={send} disabled={sending} className="w-full h-11 bg-orange-600 hover:bg-orange-700 text-white font-black" data-testid="notice-send-btn">
        {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Enviar aviso
      </Button>
    </section>
  );
};

const BroadcastList = ({ rows }) => (
  <section className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 card-shadow" data-testid="notice-broadcasts">
    <h2 className="font-heading text-base font-black text-slate-900 mb-3 flex items-center gap-2"><History className="w-4 h-4 text-slate-500" /> Avisos enviados</h2>
    {rows.length === 0 ? <p className="text-sm text-slate-500 italic">Nenhum aviso enviado ainda.</p> : (
      <ul className="divide-y divide-slate-100">
        {rows.map((b) => (
          <li key={b.id} className="py-2.5" data-testid={`broadcast-${b.id}`}>
            <div className="flex items-start gap-2">
              <p className="flex-1 min-w-0 text-sm font-bold text-slate-900">{b.title}</p>
              <span className="text-[10.5px] text-slate-500 shrink-0">{fmt(b.created_at)}</span>
            </div>
            <p className="text-xs text-slate-600 line-clamp-2">{b.body}</p>
            <p className="text-[11px] text-slate-500 mt-1">
              {b.all_schools ? "Todas as escolas" : (b.school_names || []).join(", ") + (b.recipients > 5 ? "…" : "")} • {b.recipients} escola(s) • {b.push_recipients} com push •{" "}
              <b className="text-emerald-700"><CheckCheck className="w-3 h-3 inline" /> {b.read_count} leram</b>
            </p>
          </li>
        ))}
      </ul>
    )}
  </section>
);

const SentList = ({ rows }) => (
  <section className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 card-shadow" data-testid="notice-sent">
    <h2 className="font-heading text-base font-black text-slate-900 mb-3">Todas as notificações enviadas às escolas</h2>
    <ul className="divide-y divide-slate-100">
      {rows.map((n) => (
        <li key={n.id} className="py-2 flex items-start gap-2 text-xs" data-testid={`sent-${n.id}`}>
          <span className="shrink-0 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-bold text-[10px]">{TYPE_LABEL[n.type] || n.type}</span>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-800 truncate">{n.title}</p>
            <p className="text-[10.5px] text-slate-500 truncate">{n.school_name}{n.budget_number ? ` • Orç. Nº ${n.budget_number}` : ""}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[10.5px] text-slate-500">{fmt(n.created_at)}</p>
            <p className="text-[10.5px] flex items-center gap-1 justify-end">
              {n.school_has_push ? <BellRing className="w-3 h-3 text-emerald-600" /> : <BellOff className="w-3 h-3 text-slate-300" />}
              {n.read ? <span className="text-emerald-700 font-bold">lida {fmt(n.read_at)}</span> : <span className="text-amber-700 font-bold">não lida</span>}
            </p>
          </div>
        </li>
      ))}
    </ul>
  </section>
);

export default function Notificacoes() {
  const [schools, setSchools] = useState([]);
  const [stats, setStats] = useState(null);
  const [broadcasts, setBroadcasts] = useState([]);
  const [sent, setSent] = useState([]);
  const [tab, setTab] = useState("enviar");

  const load = async () => {
    const [s, st, b, n] = await Promise.all([
      api.get("/schools"), api.get("/admin/notices/push-stats"), api.get("/admin/notices/broadcasts"), api.get("/admin/notices/sent"),
    ]);
    setSchools((Array.isArray(s.data) ? s.data : s.data?.items || []).slice().sort((a, b2) => (a.name || "").localeCompare(b2.name || "")));
    setStats(st.data); setBroadcasts(b.data || []); setSent(n.data || []);
  };
  useEffect(() => { load().catch(() => toast.error("Falha ao carregar notificações")); }, []);

  return (
    <div className="space-y-4 max-w-3xl pb-16" data-testid="notificacoes-page">
      <div>
        <h1 className="font-heading text-xl sm:text-2xl font-black text-slate-900">Notificações</h1>
        <p className="text-xs sm:text-sm text-slate-500">Avisos para as escolas e histórico de tudo que foi enviado.</p>
      </div>
      {stats && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-900 flex items-center gap-2" data-testid="notice-push-stats">
          <BellRing className="w-4 h-4" /> <b>{stats.schools_with_push}</b> de {stats.total_schools} escolas com push ativo no celular
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[["enviar", "Enviar aviso"], ["enviadas", "Enviadas"], ["site", "Contatos do site"], ["problemas", "Problemas"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`notice-tab-${k}`}
            className={`h-10 rounded-xl text-sm font-black transition-colors ${tab === k ? "bg-[#1e3a8a] text-white" : "bg-white border border-slate-200 text-slate-700"}`}>{l}</button>
        ))}
      </div>
      {tab === "enviar" ? (
        <>
          <SendForm schools={schools} stats={stats} onSent={load} />
          <BroadcastList rows={broadcasts} />
        </>
      ) : tab === "site" ? <SiteLeads /> : tab === "problemas" ? <ProblemReports /> : <SentList rows={sent} />}
    </div>
  );
}
